import { useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import { isAbsoluteUrl } from '@/helpers/url'

const WithQueryParams = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  return function WithQueryParamsWrapper(props: P & { href: string }) {
    // browser url queryString values
    const urlParams = useSearchParams()
    const getUrl = useCallback(() => {
      if (isAbsoluteUrl(props.href)) {
        return new URL(props.href)
      } else {
        if (typeof window !== 'undefined') {
          return new URL(props.href, window.location.href)
        }
        return new URL(props.href, process.env.NEXT_PUBLIC_BASE_URL)
      }
    }, [props.href])

    // absolute url
    const url = getUrl()

    // queryString values from href
    const hrefParams = new URLSearchParams(url.search)

    // merge browser url queryString values with href queryString values
    const mergedParams = new URLSearchParams([...urlParams, ...hrefParams])

    // build new href with merged queryString values
    // keep href relative when possible so locale-aware <Link> components can
    // prepend the current locale
    const newHref = isAbsoluteUrl(props.href)
      ? mergedParams.size > 0
        ? `${url.origin}${url.pathname}?${mergedParams.toString()}`
        : props.href
      : mergedParams.size > 0
        ? `${url.pathname}?${mergedParams.toString()}`
        : props.href

    return (
      <WrappedComponent
        {...props}
        href={newHref}
      />
    )
  }
}

export default WithQueryParams
